/* src/layouts/insight-hub/components/toolbar/ToolSelector.js */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Download from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from '@mui/icons-material/Save';
import ReportGenService from "services/reportgen-service";
import MDBox from "components/MDBox";
import { Card } from "@mui/material";
import MDTypography from "components/MDTypography";
import Tooltip from "@mui/material/Tooltip";

// 🚀 IMPORT UPGRADE POPUP
import UpgradePopup from "layouts/components/Upgrade";
import ShareChartButton from "./ShareChartButton";
import LiveDemoRestriction from "layouts/components/Upgrade/liveDemo"; // [NEW]
import { usePermissions } from "context"; // [FIXED]

function ToolSelector({
    chartIds,
    selectedProfiles,
    dateRange,
    isPredictionEnabled,
    contentChartRef,
    onAddProfile,
    onScheduleReport, // [NEW]
    entitlements, // ✅ NEW PROP
    chartViewModes, // ✅ NEW PROP from InsightHub
}) {
    // 🔒 LOCAL UPGRADE STATE
    const [upgradeState, setUpgradeState] = useState({ open: false, featureName: "", planRequired: "" });

    // [NEW] RBAC State
    const [rbacState, setRbacState] = useState({ open: false, featureName: "", roleRequired: "" });
    const { hasPermission, requiredRoles } = usePermissions();

    // ⏳ LOADING STATE
    const [isDownloading, setIsDownloading] = useState(false);
    const [showDemoPopup, setShowDemoPopup] = useState(false); // [NEW] Demo Popup State
    const [demoFeature, setDemoFeature] = useState(""); // [NEW] Name of the demo feature blocked

    // 🔘 MENU STATE
    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    // 🔒 DERIVE LOCKED STATE FOR DOWNLOAD
    const DOWNLOAD_FEATURE_KEY = "Report Download";
    const isDownloadLocked = entitlements?.features && !entitlements.features.includes(DOWNLOAD_FEATURE_KEY);
    const requiredDownloadPlan = entitlements?.requiredPlans?.[DOWNLOAD_FEATURE_KEY] || "Higher";

    // 🔒 DERIVE LOCKED STATE FOR SHARING
    const SHARE_FEATURE_KEY = "Report Sharing";
    const isShareLocked = entitlements?.features && !entitlements.features.includes(SHARE_FEATURE_KEY);
    const requiredSharePlan = entitlements?.requiredPlans?.[SHARE_FEATURE_KEY] || "Higher";

    // OPEN POPUP HELPER
    const handleUpgrade = (featureName, planRequired) => {
        setUpgradeState({ open: true, featureName, planRequired });
    };

    // [NEW] RBAC HELPER
    const handleRBAC = (featureName, permission = "reports:manage") => {
        setRbacState({
            open: true,
            featureName,
            roleRequired: requiredRoles[permission] || "Analyst"
        });
    };

    const handleDownloadClick = (event) => {
        // [NEW] RBAC CHECK
        if (!hasPermission("reports:view")) {
            handleRBAC("Downloading Reports", "reports:view");
            return;
        }

        // 🛑 CHECK LOCK
        if (isDownloadLocked) {
            handleUpgrade(DOWNLOAD_FEATURE_KEY, requiredDownloadPlan);
            return;
        }
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleDownload = async (format = "pdf") => {
        handleMenuClose(); // Close menu immediately

        // [NEW] Live Demo Restriction
        const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
        if (isDemo) {
            setDemoFeature("Downloading Report");
            setShowDemoPopup(true);
            return;
        }

        try {
            setIsDownloading(true); // ⏳ START LOADING

            if (!selectedProfiles || selectedProfiles.length === 0) {
                console.error("No profiles selected for report.");
                return;
            }

            if (!chartIds || chartIds.length === 0) {
                console.error("No charts selected or active for report generation.");
                return;
            }

            const CONTENT_CHART_ID = "content-performance-over-time-chart";
            const finalChartConfigs = {};

            // 1. Inject Daily/Monthly View Modes for ALL charts
            chartIds.forEach(id => {
                finalChartConfigs[id] = {
                    viewMode: chartViewModes?.[id] || "daily" // Default to daily
                };
            });

            const isContentChartActive = chartIds.includes(CONTENT_CHART_ID);

            if (isContentChartActive && contentChartRef && contentChartRef.current) {
                const chartInstance = contentChartRef.current;
                if (typeof chartInstance.getCurrentConfig === 'function') {
                    const config = chartInstance.getCurrentConfig();
                    if (config && config.entityIdOrder?.length > 0) {
                        // Merge with existing viewMode config
                        finalChartConfigs[CONTENT_CHART_ID] = {
                            ...finalChartConfigs[CONTENT_CHART_ID],
                            ...config
                        };
                    }
                }
            }

            const payloadProfileData = selectedProfiles.map(profile => ({
                profileId: profile.id,
                platform: profile.platform,
                name: profile.name,
                profilePictureUrl: profile.profilePictureUrl,
                fromDate: dateRange[0].toISOString(),
                toDate: dateRange[1].toISOString(),
            }));

            await ReportGenService.generateReport(
                payloadProfileData,
                isPredictionEnabled,
                format,
                chartIds,
                finalChartConfigs
            );

            console.log(`PDF generation request sent to backend for ${chartIds.length} charts.`, { chartConfigs: finalChartConfigs });

        } catch (error) {
            console.error("Failed to generate PDF:", error);
        } finally {
            setIsDownloading(false); // ⏳ END LOADING
        }
    };

    const renderButton = (onClick, icon, tooltip, alt, isLoading = false) => (
        <Tooltip title={tooltip}>
            <Card
                onClick={isLoading ? null : onClick} // Disable click while loading
                sx={{
                    cursor: isLoading ? "default" : "pointer",
                    width: 48,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    boxShadow: 1,
                    border: "1px solid #ddd",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                        transform: isLoading ? "none" : "translateY(-2px)",
                        boxShadow: isLoading ? 1 : 3,
                        backgroundColor: isLoading ? "#ffffff" : "#f8f9fa",
                    },
                    "&:active": {
                        backgroundColor: isLoading ? "#ffffff" : "#e9ecef",
                        boxShadow: 1,
                    },
                }}
            >
                <IconButton sx={{ padding: 0 }} disabled={isLoading}>
                    {isLoading ? (
                        <CircularProgress size={22} color="accent" />
                    ) : typeof icon === "string" ? (
                        <img src={icon} alt={alt} style={{ width: "22px", height: "22px" }} />
                    ) : (
                        React.cloneElement(icon, { sx: { color: "#000000" } })
                    )}
                </IconButton>
            </Card>
        </Tooltip>
    );

    return (
        <MDBox sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <MDTypography
                variant="button"
                fontWeight="medium"
                color="text"
                sx={{ marginBottom: 1, display: "block", textAlign: "center" }}
            >
                Tools
            </MDTypography>

            <MDBox
                sx={{
                    borderRadius: "16px",
                    backgroundColor: "#f0f2f5",
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    width: "fit-content",
                    mx: "auto",
                }}
            >
                {/* EDIT REPORT BUTTON */}
                {renderButton(() => {
                    if (!hasPermission("reports:manage")) {
                        handleRBAC("Editing Reports");
                        return;
                    }
                    onAddProfile();
                }, <EditIcon />, "Edit Report", "Edit Report")}

                {/* SAVE REPORT BUTTON */}
                {renderButton(() => {
                    const isDemo = sessionStorage.getItem('demo_role_cache') === "demo_viewer";
                    if (isDemo) {
                        setDemoFeature("Saving Report");
                        setShowDemoPopup(true);
                        return;
                    }
                    if (!hasPermission("reports:manage")) {
                        handleRBAC("Saving Reports");
                        return;
                    }
                    onScheduleReport();
                }, <SaveIcon />, "Save Report", "Save Report")}

                {/* SHARE BUTTON */}
                <ShareChartButton
                    chartIds={chartIds}
                    selectedProfiles={selectedProfiles}
                    dateRange={dateRange}
                    isPredictionEnabled={isPredictionEnabled}
                    contentChartRef={contentChartRef}
                    isLocked={isShareLocked}
                    onLockedClick={() => handleUpgrade(SHARE_FEATURE_KEY, requiredSharePlan)}
                    onRBACClick={() => handleRBAC("Sharing Reports", "reports:view")}
                    hasPermission={hasPermission("reports:view")}
                />

                {/* DOWNLOAD BUTTON */}
                {renderButton(handleDownloadClick, <Download />, "Download Report", "Download Report", isDownloading)}

                {/* 🔘 DOWNLOAD MENU */}
                <Menu
                    anchorEl={anchorEl}
                    open={openMenu}
                    onClose={handleMenuClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    disableScrollLock={true}
                    sx={{
                        "& .MuiPaper-root": {
                            borderRadius: "12px",
                            boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                            mt: 1,
                            minWidth: 180
                        }
                    }}
                >
                    <MenuItem onClick={() => handleDownload("pdf")}>
                        <ListItemIcon>
                            <PictureAsPdfIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Download PDF</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleDownload("excel")}>
                        <ListItemIcon>
                            <TableViewIcon fontSize="small" sx={{ color: "#1b5e20" }} />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Download Excel</ListItemText>
                    </MenuItem>
                </Menu>
            </MDBox>

            {/* 🔒 UPGRADE POPUP */}
            <UpgradePopup
                open={upgradeState.open}
                onClose={() => setUpgradeState({ ...upgradeState, open: false })}
                featureName={upgradeState.featureName}
                planRequired={upgradeState.planRequired}
            />

            {/* [NEW] Live Demo Restriction Popup */}
            <LiveDemoRestriction
                open={showDemoPopup}
                onClose={() => setShowDemoPopup(false)}
                featureName={demoFeature}
            />

            {/* [NEW] RBAC Restriction Popup */}
            <UpgradePopup
                open={rbacState.open}
                onClose={() => setRbacState({ ...rbacState, open: false })}
                type="rbac"
                featureName={rbacState.featureName}
                roleRequired={rbacState.roleRequired}
            />
        </MDBox>
    );
}

ToolSelector.propTypes = {
    chartIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedProfiles: PropTypes.array.isRequired,
    dateRange: PropTypes.array.isRequired,
    isPredictionEnabled: PropTypes.bool.isRequired,
    contentChartRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.object }),
    ]).isRequired,
    onAddProfile: PropTypes.func.isRequired,
    onScheduleReport: PropTypes.func,
    entitlements: PropTypes.object,
    chartViewModes: PropTypes.object // ✅ Validated
};

export default ToolSelector;